import React from "react";
import ReactDOM from "react-dom/client";
import Highcharts from "highcharts";
import HighchartsStock from "highcharts/highstock";
import {
  ConfirmProvider,
  ErrorBoundary,
  initBranding,
  reloadOnce,
  ToastProvider,
} from "@digitalisation/enlixy-comp";
import { ConditionalAuthProvider } from "./auth/ConditionalAuthProvider";
import { BRANDING_STORAGE_KEY } from "./branding/orgBranding";
import { AppTextsProvider } from "./providers/AppTextsProvider";
import { initCockpitTheme } from "./providers/ThemeProvider";
import App from "./App.tsx";
import "./index.css";
import "./i18n";

// Display all chart timestamps in the browser's local timezone (not UTC).
// Must be set on both highcharts and highcharts/highstock as they are separate instances.
const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
Highcharts.setOptions({ time: { timezone: localTz } });
HighchartsStock.setOptions({ time: { timezone: localTz } });

// The theme and the organization's branding of the last visit, before the
// first render (no flash of the light or the Focke look).
initCockpitTheme();
initBranding({ storageKey: BRANDING_STORAGE_KEY });

// After a deploy the code parts of the previous version are gone: reload once.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnce()) event.preventDefault();
});

const isDevelopment = import.meta.env.DEV;

// Shared providers (@digitalisation/enlixy-comp): texts in the cockpit's
// language, a full-page error view instead of a white page, toasts and the
// in-app confirmation dialog.
const AppWrapper = () => (
  <AppTextsProvider>
    <ErrorBoundary full>
      <ToastProvider>
        <ConfirmProvider>
          <ConditionalAuthProvider>
            <App />
          </ConditionalAuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </ErrorBoundary>
  </AppTextsProvider>
);

ReactDOM.createRoot(document.getElementById("root")!).render(
  isDevelopment ? (
    <AppWrapper />
  ) : (
    <React.StrictMode>
      <AppWrapper />
    </React.StrictMode>
  ),
);
